import { Fragment, type MouseEvent as ReactMouseEvent, useCallback, useEffect, useMemo } from "react";

import { type ConsentOptionsListType, ConsentSection } from "@ui/components/ConsentSection";
import { Form } from "@ui/components/Form";
import { useConsentReminder } from "@ui/hooks/useConsentReminder";
import { useUser } from "@ui/hooks/useUser";
import { renderListSeparator } from "@ui/utils/renderListSeparator";

import { SupportingText } from "@jti/core-components/components/SupportingText";
import { useOtPurposes } from "@jti/rest-api";

import { LegalText } from "../LegalText";

import { useConsentReminderForm } from "./hooks/useConsentReminderForm";
import { getDisplayedConsents, getDisplayedConsentSections } from "./utils/consentReminder";

import type { ConsentReminderDialogProps } from "./ConsentReminderDialog";
import { ConsentReminderDialog } from "./ConsentReminderDialog";
import {
  type ConsentReminderCloseReason,
  trackConsentReminderClose,
  trackConsentReminderCtaClick,
} from "./consentReminderTracking";

import styles from "./ConsentReminderDialogContainer.module.scss";

export type ConsentReminderDialogContainerProps = Omit<
  ConsentReminderDialogProps,
  "isOpened" | "onPrimaryButtonClick" | "onSecondaryButtonClick" | "isPrimaryButtonPending" | "onClose"
> & {
  isEnabled: boolean;
  forceOpened?: boolean;
  maximumOccurrencesPerYear?: number;
  consentExpirationLimit?: number;
  popUpDelay?: number;
  offerConsentName?: string;
  productInformationConsentName?: string;
  ploomClubConsentName?: string;
  consentsLegalText?: string;
  consentOptionsListType?: ConsentOptionsListType;
  isMarketingConsentsConfirmationEnabled?: boolean;
};

export const ConsentReminderDialogContainer = ({
  isEnabled,
  forceOpened = false,
  maximumOccurrencesPerYear = 0,
  consentExpirationLimit = 0,
  popUpDelay = 0,
  offerConsentName,
  productInformationConsentName,
  ploomClubConsentName,
  consentsLegalText,
  consentOptionsListType = "default",
  isMarketingConsentsConfirmationEnabled = false,
  primaryButtonHref,
  secondaryButtonHref,
  ...dialogProps
}: ConsentReminderDialogContainerProps) => {
  const { user, isUserLoggedIn, isCheckingUser } = useUser();
  const uuid = user?.uuid;
  const isConsentQueryEnabled = isEnabled && isUserLoggedIn && !isCheckingUser;
  const {
    purposes: userConsents,
    isLoading,
    isError,
    refetch,
  } = useOtPurposes("all", "section", undefined, undefined, isConsentQueryEnabled);
  const { isConsentReminderVisible, consentTypes, closeConsentReminder } = useConsentReminder({
    config: {
      isEnabled: isEnabled && isUserLoggedIn,
      maximumOccurrencesPerYear,
      consentExpirationLimit,
      popUpDelay,
      offerConsentName,
      productInformationConsentName,
      ploomClubConsentName,
    },
    consents: userConsents,
    isLoading: isLoading || isCheckingUser,
    isError,
  });
  const displayedConsents = useMemo(
    () =>
      getDisplayedConsents({
        consentTypes,
        consents: userConsents,
        offerConsentName,
        productInformationConsentName,
        ploomClubConsentName,
      }),
    [consentTypes, offerConsentName, ploomClubConsentName, productInformationConsentName, userConsents]
  );
  const displayedConsentSections = useMemo(
    () => getDisplayedConsentSections(displayedConsents, consentOptionsListType),
    [consentOptionsListType, displayedConsents]
  );
  const {
    form,
    submissionError,
    isActionsBlocked,
    confirmChoices: handleConfirmChoices,
    submit: handleSubmit,
  } = useConsentReminderForm({
    displayedConsents,
    displayedConsentSections,
    uuid,
    refetchConsents: refetch,
    closeConsentReminder,
  });
  const isClosable = Boolean(dialogProps.closable && !isActionsBlocked);
  const isOpened = forceOpened || isConsentReminderVisible;

  const handleClose = useCallback(
    (closeReason: ConsentReminderCloseReason) => {
      trackConsentReminderClose(closeReason, dialogProps.title);

      if (!isActionsBlocked) {
        closeConsentReminder();
      }
    },
    [closeConsentReminder, dialogProps.title, isActionsBlocked]
  );
  const handleCloseButtonClick = useCallback(() => handleClose("x_button"), [handleClose]);

  const handlePrimaryButtonClick = async (event?: ReactMouseEvent<HTMLElement>) => {
    if (isMarketingConsentsConfirmationEnabled) {
      trackConsentReminderCtaClick(dialogProps.primaryButtonText);
      await handleSubmit();
      return;
    }

    if (primaryButtonHref) {
      event?.preventDefault();
      await trackConsentReminderCtaClick(dialogProps.primaryButtonText);
      window.location.assign(primaryButtonHref);
      return;
    }

    trackConsentReminderCtaClick(dialogProps.primaryButtonText);
  };

  const handleSecondaryButtonClick = async (event?: ReactMouseEvent<HTMLElement>) => {
    const { secondaryButtonText } = dialogProps;
    if (!secondaryButtonText) return;

    const trackSecondaryButtonClick = () =>
      Promise.all([
        trackConsentReminderCtaClick(secondaryButtonText),
        trackConsentReminderClose("cta_click", dialogProps.title),
      ]);

    if (isMarketingConsentsConfirmationEnabled) {
      trackSecondaryButtonClick();

      if (!isActionsBlocked) {
        closeConsentReminder();
      }
      return;
    }

    if (secondaryButtonHref) {
      event?.preventDefault();
      await trackSecondaryButtonClick();
      window.location.assign(secondaryButtonHref);
      return;
    }

    trackSecondaryButtonClick();
  };

  const handleBackdropClose = useCallback(() => handleClose("outside_click"), [handleClose]);

  useEffect(() => {
    if (!isOpened || !isClosable) {
      return;
    }

    const dialogElement = document.querySelector<HTMLDialogElement>(
      `[data-testid="${dialogProps.dataTestId ?? "consentReminderDialog"}"]`
    );

    if (!dialogElement) {
      return;
    }

    const handleBackdropClick = (event: MouseEvent) => {
      if (event.target === dialogElement) {
        handleBackdropClose();
      }
    };

    dialogElement.addEventListener("click", handleBackdropClick);

    return () => {
      dialogElement.removeEventListener("click", handleBackdropClick);
    };
  }, [dialogProps.dataTestId, handleBackdropClose, isClosable, isOpened]);

  const dialogActionProps = isMarketingConsentsConfirmationEnabled
    ? {
        onPrimaryButtonClick: handlePrimaryButtonClick,
        onSecondaryButtonClick: handleSecondaryButtonClick,
      }
    : {
        primaryButtonHref,
        secondaryButtonHref,
        onPrimaryButtonClick: handlePrimaryButtonClick,
        onSecondaryButtonClick: handleSecondaryButtonClick,
      };

  if (!forceOpened && !isEnabled) {
    return null;
  }

  return (
    <ConsentReminderDialog
      {...dialogProps}
      {...dialogActionProps}
      isOpened={isOpened}
      isPrimaryButtonPending={isActionsBlocked}
      isSecondaryButtonPending={isActionsBlocked || dialogProps.isSecondaryButtonPending}
      closable={isClosable}
      onClose={handleCloseButtonClick}
    >
      {isMarketingConsentsConfirmationEnabled && (
        <Form form={form} onSubmit={handleConfirmChoices}>
          {displayedConsentSections.map((section, sectionNumber) => (
            <Fragment key={section.purposeId}>
              <ConsentSection
                section={section}
                formPrefix="consents"
                disabled={isActionsBlocked}
                required={false}
                isColumnDisplayOfConsents={true}
              />
              {renderListSeparator(displayedConsentSections.length, sectionNumber)}
            </Fragment>
          ))}
          {submissionError && (
            <div className={styles.submissionError}>
              <SupportingText variant="error" role="alert">
                {submissionError}
              </SupportingText>
            </div>
          )}
          <LegalText legalText={consentsLegalText} className={styles.legalText} />
        </Form>
      )}
    </ConsentReminderDialog>
  );
};
